import React, { useEffect, useRef, useState } from "react";
import { Alert, ButtonGroup, Col, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { Awesome } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { arrayMove, url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
    });

    const style = {
        opacity: isDragging ? 0.4 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
        cursor: "move",
    };

    return (
        <div {...listeners} {...attributes} ref={setNodeRef} style={style} className={`tag tag_${props.value.group}`}>
            {props.children}
        </div>
    );
};

const MetricsTags = ({ metrics, selectMetricsHandle, setSelectedMetrics }) => {
    const { t } = useTranslation();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { delay: 100, distance: 5 }, // Хак, чтобы onClick на дочернем компоненте срабатывал
        })
    );

    const onDragEndHandle = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = metrics.findIndex((i) => i.id === active.id);
            const newIndex = metrics.findIndex((i) => i.id === over.id);

            const newSelected = arrayMove(metrics, oldIndex, newIndex);
            setSelectedMetrics(newSelected);
        }
    };

    return (
        <div className="select-tags text-secondary">
            {metrics?.length === 0 && t("Выберите метрики ниже")}
            {metrics?.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndHandle}>
                    <SortableContext items={metrics} strategy={horizontalListSortingStrategy}>
                        <div className="select-tags__list">
                            {metrics.map((item, index) => (
                                <SortableItem id={item.id} key={`${item.id}-${index}`} value={item}>
                                    <>
                                        <div className="tag__content">{item.title}</div>
                                        <div
                                            onClick={() =>
                                                selectMetricsHandle({
                                                    id: item.id,
                                                    title: item.title,
                                                    group: item.group,
                                                })
                                            }
                                            className="tag__close"
                                        >
                                            ×
                                        </div>
                                    </>
                                </SortableItem>
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};

const MetricsColumn = ({ title, data, group, selectMetricsHandle, selectedMetrics }) => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const [isCollapsed, setIsCollapsed] = useState();

    const collapseHandle = (e) => {
        e.preventDefault();
        setIsCollapsed(!isCollapsed);
    };

    useEffect(() => {
        if (ref.current?.clientHeight > 254) {
            setIsCollapsed(true);
        }
    }, []);

    if (!data) {
        return <></>;
    }

    return (
        <div ref={ref} className={`selector selector_${group} card ` + (isCollapsed && "is-collapsed")}>
            <div className="selector__header card-header p-2">{title}</div>
            <div className="selector__content card-body p-2">
                {data.map((selector) => (
                    <div className="d-flex align-center mb-2" key={selector.id}>
                        <DevForm.Checkbox
                            type="checkbox"
                            id={`info-${selector.id}`}
                            className="mr-2"
                            label={selector.title}
                            onChange={() =>
                                selectMetricsHandle({
                                    id: selector.id,
                                    title: selector.title,
                                    group,
                                })
                            }
                            checked={selectedMetrics.some((item) => item.id === selector.id)}
                        />
                        {selector?.tooltip && (
                            <span className="text-secondary">
                                <Awesome type="question-circle" tooltip={selector.tooltip} />
                            </span>
                        )}
                    </div>
                ))}
            </div>
            {isCollapsed !== undefined && (
                <a
                    href="#"
                    className="selector__expand card-footer p-2"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                    }}
                    onClick={collapseHandle}
                >
                    <span className="mr-2">{isCollapsed ? t("Развернуть") : t("Свернуть")}</span>
                    <span className="selector__expand-icon">
                        <Awesome type="angle-down" />
                    </span>
                </a>
            )}
        </div>
    );
};

const ReportTableHeader = ({ data, setData, selectors }) => {
    const [sortField, setSortField] = useState("");
    const [order, setOrder] = useState("asc");

    const handleSorting = (field, sortOrder) => {
        if (field) {
            const newArray = { ...data };
            const sortableArray = newArray.data;
            newArray.data = [...sortableArray].sort((a, b) => {
                return (
                    a[sortField].toString().localeCompare(b[sortField].toString(), "en", {
                        numeric: true,
                    }) * (sortOrder === "asc" ? 1 : -1)
                );
            });
            setData(newArray);
        }
    };

    const handleSortingChange = (accessor) => {
        const sortOrder = accessor === sortField && order === "asc" ? "desc" : "asc";
        setSortField(accessor);
        setOrder(sortOrder);
        handleSorting(accessor, sortOrder);
    };

    return (
        <thead>
            <tr>
                {selectors &&
                    selectors?.map((selector) => (
                        <th
                            key={selector.id}
                            className="text-nowrap sticky-top"
                            style={{ backgroundColor: "#eee" }}
                            onClick={() => handleSortingChange(selector.id)}
                        >
                            {selector.title}{" "}
                            {sortField === selector.id && order === "asc" ? (
                                <Awesome type="chevron-down" />
                            ) : sortField === selector.id && order === "desc" ? (
                                <Awesome type="chevron-up" />
                            ) : (
                                ""
                            )}
                        </th>
                    ))}
            </tr>
        </thead>
    );
};

const ReportTable = ({ reportData, reportCurrency }) => {
    const { t } = useTranslation();
    const [data, setData] = useState();

    useEffect(() => {
        if (reportData) {
            setData(reportData);
        }
    }, [reportData]);

    if (!reportData) {
        return <></>;
    }

    return (
        <div className="table-responsive mt-5" style={{ maxHeight: "600px" }}>
            <Table bordered hover size="sm">
                <ReportTableHeader data={data} setData={setData} selectors={reportData.selectors} />
                <tbody>
                    {data?.data &&
                        data.data.map((item, index) => (
                            <tr key={`${index}-report-tr`}>
                                {Object.keys(item).map((reportDataKey, i) => (
                                    <td key={`${index}-${i}-${reportDataKey}`}>{item[reportDataKey]}</td>
                                ))}
                            </tr>
                        ))}
                </tbody>
                {reportData?.totals && (
                    <tfoot>
                        <tr>
                            {reportData.selectors.map((selector) => (
                                <td key={selector.id}>
                                    <b>{reportData.totals[selector.id]}</b>
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                )}
            </Table>

            <Alert variant="info">
                {t("Денежные метрики указаны в валюте")} <b>{reportCurrency}</b>
            </Alert>
        </div>
    );
};

function MyTracker({ gameId }) {
    const apiUrl = url_reverse("api:game:statistics:mytracker", { game_id: gameId });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const { t } = useTranslation();
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [reportData, setReportData] = useState();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { game } = gameCommonInfo;
    const { api_post } = useRequest();

    const gameName = game.base_locale_object.name;
    const disabledButtons =
        selectedMetrics.length === 0 || !selectedMetrics.find((i) => i.group === "metrics" || i.group === "finance");

    useEffect(() => {
        setApiData({ ...apiData, metrics: selectedMetrics.map((i) => i.id).join(",") });
    }, [selectedMetrics]);

    if (!apiData) return;

    const selectMetricsHandle = ({ id, title, group }) => {
        const arr = [...selectedMetrics];
        const newArr = arr.some((i) => i.id === id) ? arr.filter((i) => i.id !== id) : [...arr, { id, title, group }];
        setSelectedMetrics(newArr);
    };

    const createReportHandle = (ev) => {
        api_post(ev, apiUrl, {
            params: { ...apiData },
            then: (resp) => {
                if (resp.status === "ok") {
                    setReportData(resp);
                } else {
                    alert(t("При создании отчета произошла ошибка. Попробуйте еще раз"));
                }
            },
        });
    };

    const exportReportHandle = (ev) => {
        api_post(ev, apiUrl, {
            params: { ...apiData, export: true, responseType: "arrayBuffer" },
            then: (resp) => {
                const fileName = `${gameName}.xlsx`;
                const file = new Blob([resp], { type: "application/vnd.ms-excel" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(file);
                link.download = fileName;
                link.click();
                URL.revokeObjectURL(link.href);
            },
        });
    };

    return (
        <CommonCard header={gameName}>
            <FormFieldGroup label={t("Валюта")}>
                <DevForm.Select
                    defaultValue={{ value: "RUB", label: "RUB" }}
                    onChange={(value) => setApiData((prev) => ({ ...prev, currency: value.value }))}
                    choices={apiData.currencies.map((c) => ({ value: c[0], label: c[1] }))}
                />
            </FormFieldGroup>
            <FormFieldGroup label={t("Период")}>
                <DevForm.DateRangePicker
                    value={[apiData.start_date, apiData.end_date]}
                    onChange={(value) => {
                        setApiData((prev) => ({ ...prev, start_date: value[0], end_date: value[1] }));
                    }}
                />
            </FormFieldGroup>
            <MetricsTags
                metrics={selectedMetrics}
                selectMetricsHandle={selectMetricsHandle}
                setSelectedMetrics={setSelectedMetrics}
            />
            <Row>
                <Col>
                    <MetricsColumn
                        group="info"
                        data={apiData.selectors?.info}
                        title={t("Информация")}
                        selectMetricsHandle={selectMetricsHandle}
                        selectedMetrics={selectedMetrics}
                    />
                </Col>

                <Col>
                    <MetricsColumn
                        group="metrics"
                        data={apiData.selectors?.metrics}
                        title={t("Метрики проекта")}
                        selectMetricsHandle={selectMetricsHandle}
                        selectedMetrics={selectedMetrics}
                    />
                </Col>

                <Col>
                    <MetricsColumn
                        group="finance"
                        data={apiData.selectors?.finance}
                        title={t("Финансы")}
                        selectMetricsHandle={selectMetricsHandle}
                        selectedMetrics={selectedMetrics}
                    />
                </Col>
            </Row>
            <ButtonGroup className="mt-3">
                <DevForm.Button disabled={disabledButtons} name="report" onClick={createReportHandle}>
                    {reportData ? t("Обновить отчет") : t("Показать отчет")}
                </DevForm.Button>
                <DevForm.Button
                    className="btn-success"
                    disabled={disabledButtons}
                    name="export"
                    onClick={exportReportHandle}
                >
                    <Awesome type="file-excel-o" /> {t("Экспорт")}
                </DevForm.Button>
            </ButtonGroup>
            <ReportTable reportData={reportData} reportCurrency={apiData?.currency || "RUB"} />
        </CommonCard>
    );
}

export default MyTracker;
